import {
  useMemo,
  useContext,
  useState,
  useRef,
  useLayoutEffect,
  type FC,
  type MouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { KizenTypography } from '__app/typography';
import { colorsButton } from 'app/colors';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';
import IconButton from '__components/Kizen/IconButton';
import Icon, { IconSizing } from '__components/Kizen/Icon';
import { useTooltip, truncationPredicates } from '__components/Kizen/Tooltip';
import Handle from '../Handle';
import { Button as ButtonKDS } from '@kizen/kds/Button';
import { EMPTY_ARRAY } from 'utility/fieldHelpers';
import { ExecutionHistoryContext } from 'ts-components/AutomationEngine/ExecutionHistory/context';
import {
  Wrapper,
  StyledMenuSelect,
  AnchorAsWrapper,
  ColorBar,
  Header,
  Body,
  ThreeDotsMenu,
  UserDescription,
  StyledClickableLink,
  RerunContainer,
  StyledCardContent,
  StyledMenu,
} from './styles';
import { Searchable, SearchableStepId } from './Searchable';
import { Position } from 'reactflow';
import type { GenericOption } from 'pages/AutomationEngine/types';
import { StepCardProps } from './StepCard';

const overflowXOrY = (el: HTMLElement) =>
  truncationPredicates.x(el) || truncationPredicates.y(el);

const MENU_OPTION_HEIGHT = 32;
const MIN_VISIBLE_OPTIONS = 5;

const CardMenu: FC<{
  menuOptions: GenericOption[];
  onSelectMenu?: null | ((option: GenericOption) => void);
  editOnly?: boolean;
  disableMenu?: boolean;
}> = ({ menuOptions, onSelectMenu, editOnly, disableMenu }) => {
  const { t } = useTranslation();

  const buttonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<Element | null>(null);

  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    top: string | number;
    bottom: string | number;
    left: string | number;
    right: string | number;
  } | null>(null);

  const requiredMenuHeight =
    Math.min(menuOptions.length, MIN_VISIBLE_OPTIONS) * MENU_OPTION_HEIGHT;

  const onButtonClick = () => {
    if (editOnly) {
      onSelectMenu?.({ value: 'edit', label: t('Edit') });
    } else {
      setShowMenu(true);
    }
  };

  useLayoutEffect(() => {
    if (showMenu) {
      let frameId: number | null = null;
      let lastRect: DOMRect | null = null;
      const button = buttonRef.current;
      const container = document.querySelector('.FlowContainer');
      containerRef.current = container;

      const syncPosition = () => {
        if (button && container) {
          const rect = button.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          if (
            !lastRect ||
            lastRect.top !== rect.top ||
            lastRect.right !== rect.right
          ) {
            lastRect = rect;
            const spaceBelow = window.innerHeight - rect.bottom;
            const openAbove = spaceBelow < requiredMenuHeight;
            setMenuPosition({
              top: openAbove ? 'auto' : rect.bottom - containerRect.top - 2,
              bottom: openAbove ? window.innerHeight - rect.top : 'auto',
              right: window.innerWidth - rect.left - rect.width,
              left: 'auto',
            });
          }
        }
        frameId = requestAnimationFrame(syncPosition);
      };
      syncPosition();

      const handleCloseMenu = (e: Event) => {
        const target = e.target as Node | null;
        if (
          !buttonRef.current?.contains(target) &&
          !menuRef.current?.contains(target)
        ) {
          setShowMenu(false);
        }
      };
      document.addEventListener('click', handleCloseMenu, {
        capture: true,
      });
      return () => {
        document.removeEventListener('click', handleCloseMenu, {
          capture: true,
        });
        if (frameId) {
          cancelAnimationFrame(frameId);
        }
      };
    }
  }, [showMenu, requiredMenuHeight]);

  return (
    <ThreeDotsMenu ref={buttonRef}>
      <IconButton
        title={t('Edit Item')}
        disabled={disableMenu}
        onClick={onButtonClick}
        color={
          showMenu
            ? 'var(--color-button-tertiary-hover)'
            : colorsButton.iconGray
        }
        sizing="dense"
      >
        <IconSizing size={editOnly ? '12px' : '15px'}>
          <Icon icon={editOnly ? 'pen' : 'three-dot'} />
        </IconSizing>
      </IconButton>
      {editOnly || !showMenu || !menuPosition
        ? null
        : createPortal(
            <StyledMenu ref={menuRef} style={menuPosition}>
              <StyledMenuSelect
                menuOnly
                menuIsOpen
                onChange={(val: GenericOption) => {
                  setShowMenu(false);
                  onSelectMenu?.(val);
                }}
                options={menuOptions}
                data-qa="card-action-menu"
              />
            </StyledMenu>,
            containerRef.current!
          )}
    </ThreeDotsMenu>
  );
};

export const StepCardOld: FC<
  StepCardProps & {
    cardComponentType?: 'div' | 'anchor';
    title?: string;
    color?: string;
  }
> = ({
  color,
  title: titleProp,
  panel = null,
  userDescription = null,
  addMenu = true,
  onSelectMenu,
  disableMenu = false,
  children,
  menuOptions = EMPTY_ARRAY,
  showMovingPlaceholder,
  cardComponentType = 'div',
  showStepId,
  editOnly = false,
  showRestart,
  leading = null,
  isSmartConnectorFlow = false,
  borderColor = null,
  id,
  ...props
}) => {
  const { t } = useTranslation();
  const { setRerunModalStepId } = useContext(ExecutionHistoryContext);
  const titleTranslate = useMemo(
    (): Record<string, string> => ({
      Action: t('Action'),
      Condition: t('Condition'),
      Delay: t('Delay'),
      Goal: t('Goal'),
      Trigger: t('Trigger'),
      'Initialize Variables': t('Initialize Variables'),
      Variable: t('Variable'),
      'AI Agent': t('AI Agent'),
    }),
    [t]
  );
  const title = (titleProp && titleTranslate[titleProp]) || titleProp;

  const [tooltipProps, tooltip] = useTooltip({ html: true }, overflowXOrY);
  const [descriptionTooltipProps, descriptionTooltip] = useTooltip(
    { html: true },
    overflowXOrY
  );

  const WrapperComponent =
    cardComponentType === 'div' ? Wrapper : AnchorAsWrapper;

  const onSelectEdit = (e: MouseEvent<HTMLDivElement>) => {
    e?.preventDefault();
    onSelectMenu?.({ value: 'edit', label: t('Edit') });
  };

  const hasEditOption = useMemo(() => {
    return addMenu && menuOptions.some((option) => option.value === 'edit');
  }, [addMenu, menuOptions]);

  const enableEditLink = hasEditOption && !disableMenu;

  return (
    <WrapperComponent {...props} color={color}>
      {leading}
      <StyledCardContent hasBorder={!!borderColor}>
        <ColorBar color={color} />
        <Header>
          <div onClick={enableEditLink ? onSelectEdit : undefined}>
            <Searchable stepId={id} enabled={!isSmartConnectorFlow}>
              {enableEditLink ? (
                <StyledClickableLink weight="bold" type="anchor" href="">
                  {title}
                </StyledClickableLink>
              ) : (
                <TextEllipsisWithTooltip weight="bold">
                  {title}
                </TextEllipsisWithTooltip>
              )}
            </Searchable>
          </div>
          {addMenu ? (
            <CardMenu
              menuOptions={menuOptions}
              onSelectMenu={onSelectMenu}
              disableMenu={disableMenu}
              editOnly={editOnly}
            />
          ) : showRestart || showStepId ? (
            <RerunContainer>
              {showStepId ? (
                <SearchableStepId stepId={id} enabled={!isSmartConnectorFlow} />
              ) : null}
              {showRestart ? (
                <ButtonKDS
                  onClick={() => setRerunModalStepId(id)}
                  leftIcon="action-automation-debug-rerun-step"
                  variant="text"
                  color="secondary"
                  size="small"
                />
              ) : null}
            </RerunContainer>
          ) : null}
        </Header>
        {children && (
          <>
            <Body {...tooltipProps} data-qa="card-body">
              <Searchable stepId={id} enabled={!isSmartConnectorFlow}>
                {typeof children !== 'string' && children}
                {typeof children === 'string' && (
                  <KizenTypography as="span">{children}</KizenTypography>
                )}
              </Searchable>
            </Body>
            {tooltip}
          </>
        )}
        {userDescription && (
          <>
            <UserDescription
              {...descriptionTooltipProps}
              data-qa="user-description"
            >
              <Searchable stepId={id} enabled={!isSmartConnectorFlow}>
                <KizenTypography as="span">{userDescription}</KizenTypography>
              </Searchable>
            </UserDescription>
            {descriptionTooltip}
          </>
        )}
        <Handle
          id="handle-left"
          type="target"
          position={Position.Left}
          style={{
            left: 0,
          }}
        />
        <Handle
          id="handle-right"
          type="source"
          position={Position.Right}
          style={{
            right: 0,
          }}
        />
        <Handle
          id="handle-top"
          type="target"
          position={Position.Top}
          style={{
            top: 0,
          }}
        />
        <Handle
          id="handle-bottom"
          type="source"
          position={Position.Bottom}
          style={{
            bottom: 0,
          }}
        />
        <Handle
          id="handle-bottom-target"
          type="target"
          position={Position.Bottom}
          style={{
            bottom: 0,
            right: '50%',
          }}
        />
      </StyledCardContent>
    </WrapperComponent>
  );
};
